/**
 * Texas Hold'em Store - State management for Hold'em poker
 * 2 hole cards + 5 community cards, 4 betting rounds
 */

import { create } from 'zustand';
import { 
  createDeck, 
  shuffleDeck, 
  dealCards, 
  bestFiveCardHand,
  PokerSounds
} from './pokerEngine.js';
import { STARTING_CHIPS, AI_NAMES } from './pokerConstants.js';

const BLIND_LEVELS = { SMALL: 5, BIG: 10 };

const GAME_PHASES = {
  WAITING: 'waiting',
  PREFLOP: 'preflop',
  FLOP: 'flop',
  TURN: 'turn',
  RIVER: 'river',
  SHOWDOWN: 'showdown',
  RESULT: 'result'
};

const createPlayer = (id, name, chips, isHuman = false) => ({
  id, name, chips, isHuman,
  holeCards: [],
  hand: null,
  currentBet: 0,
  totalBet: 0,
  folded: false,
  isAllIn: false,
  isDealer: false,
  isSmallBlind: false,
  isBigBlind: false,
  hasActed: false
});

const INITIAL_STATE = {
  gameMode: null,
  gamePhase: GAME_PHASES.WAITING,
  players: [],
  dealerIndex: 0,
  currentPlayerIndex: 0,
  communityCards: [],
  pot: 0,
  currentBet: 0,
  minRaise: BLIND_LEVELS.BIG,
  lastRaiseAmount: BLIND_LEVELS.BIG,
  smallBlind: BLIND_LEVELS.SMALL,
  bigBlind: BLIND_LEVELS.BIG,
  deck: [],
  message: '',
  actionLog: [],
  showdownRevealed: false,
  winners: [],
  handsPlayed: 0,
  handsWon: 0,
  // Hand history
  handHistory: [],
  currentHandActions: [],
  // Chip animation
  chipAnimation: null // { fromPosition: number, amount: number }
};

const useHoldemStore = create((set, get) => ({
  ...INITIAL_STATE,
  
  clearChipAnimation: () => set({ chipAnimation: null }),
  
  setGameMode: (mode) => {
    const players = [createPlayer(0, 'You', STARTING_CHIPS, true)];
    
    if (mode === 'ai_opponents') {
      const shuffledNames = [...AI_NAMES].sort(() => Math.random() - 0.5);
      for (let i = 0; i < 5; i++) { // 6-max table
        players.push(createPlayer(i + 1, shuffledNames[i], STARTING_CHIPS, false));
      }
    } else if (mode === 'heads_up') {
      players.push(createPlayer(1, 'Villain', STARTING_CHIPS, false));
    }
    
    players[0].isDealer = true;
    
    set({
      ...INITIAL_STATE,
      gameMode: mode,
      players,
      gamePhase: GAME_PHASES.WAITING,
      message: 'Click "Deal" to start'
    });
  },
  
  startHand: () => {
    const { players, dealerIndex, smallBlind, bigBlind, handHistory } = get();
    
    // Reset players
    const resetPlayers = players.map(p => ({
      ...p,
      holeCards: [],
      hand: null,
      currentBet: 0,
      totalBet: 0,
      folded: p.chips <= 0,
      isAllIn: false,
      hasActed: false,
      isDealer: false,
      isSmallBlind: false,
      isBigBlind: false
    }));
    
    const activePlayers = resetPlayers.filter(p => !p.folded);
    if (activePlayers.length < 2) {
      set({ message: 'Not enough players!' });
      return;
    }
    
    // Rotate dealer
    let newDealerIndex = (dealerIndex + 1) % resetPlayers.length;
    while (resetPlayers[newDealerIndex].folded) {
      newDealerIndex = (newDealerIndex + 1) % resetPlayers.length;
    }
    
    // Find SB and BB positions
    let sbIndex = (newDealerIndex + 1) % resetPlayers.length;
    while (resetPlayers[sbIndex].folded) sbIndex = (sbIndex + 1) % resetPlayers.length;
    
    let bbIndex = (sbIndex + 1) % resetPlayers.length;
    while (resetPlayers[bbIndex].folded) bbIndex = (bbIndex + 1) % resetPlayers.length;
    
    resetPlayers[newDealerIndex].isDealer = true;
    resetPlayers[sbIndex].isSmallBlind = true;
    resetPlayers[bbIndex].isBigBlind = true;
    
    // Post blinds
    const sbAmount = Math.min(smallBlind, resetPlayers[sbIndex].chips);
    const bbAmount = Math.min(bigBlind, resetPlayers[bbIndex].chips);
    
    resetPlayers[sbIndex].chips -= sbAmount;
    resetPlayers[sbIndex].currentBet = sbAmount;
    resetPlayers[sbIndex].totalBet = sbAmount;
    if (resetPlayers[sbIndex].chips === 0) resetPlayers[sbIndex].isAllIn = true;
    
    resetPlayers[bbIndex].chips -= bbAmount;
    resetPlayers[bbIndex].currentBet = bbAmount;
    resetPlayers[bbIndex].totalBet = bbAmount;
    if (resetPlayers[bbIndex].chips === 0) resetPlayers[bbIndex].isAllIn = true;
    
    const pot = sbAmount + bbAmount;
    const initialLog = [
      `--- New Hand ---`,
      `${resetPlayers[sbIndex].name} posts SB (${sbAmount})`,
      `${resetPlayers[bbIndex].name} posts BB (${bbAmount})`
    ];
    
    PokerSounds.playChip();
    
    set({
      players: resetPlayers,
      dealerIndex: newDealerIndex,
      pot,
      currentBet: bbAmount,
      minRaise: bigBlind,
      lastRaiseAmount: bigBlind,
      communityCards: [],
      gamePhase: GAME_PHASES.WAITING,
      actionLog: initialLog,
      currentHandActions: [...initialLog],
      showdownRevealed: false,
      winners: [],
      message: 'Dealing hole cards...'
    });
    
    setTimeout(() => get().dealHoleCards(), 500);
  },
  
  dealHoleCards: () => {
    const { players } = get();
    let deck = shuffleDeck(createDeck());
    
    const dealtPlayers = players.map(p => {
      if (p.folded) return p;
      const { dealt, remaining } = dealCards(deck, 2);
      deck = remaining;
      return { ...p, holeCards: dealt };
    });
    
    PokerSounds.playShuffle();
    
    set({
      players: dealtPlayers,
      deck,
      gamePhase: GAME_PHASES.PREFLOP,
      message: 'Pre-flop betting'
    });
    
    setTimeout(() => get().startBettingRound(), 800);
  },
  
  startBettingRound: () => {
    const { players, gamePhase } = get();
    
    // First to act depends on phase
    let firstToAct;
    if (gamePhase === GAME_PHASES.PREFLOP) {
      // Pre-flop: left of BB
      const bbIndex = players.findIndex(p => p.isBigBlind);
      firstToAct = (bbIndex + 1) % players.length;
    } else {
      // Post-flop: left of dealer
      const dealerIndex = players.findIndex(p => p.isDealer);
      firstToAct = (dealerIndex + 1) % players.length;
    }
    
    // Skip folded/all-in players
    let attempts = 0;
    while ((players[firstToAct].folded || players[firstToAct].isAllIn) && attempts < players.length) {
      firstToAct = (firstToAct + 1) % players.length;
      attempts++;
    }
    
    // Reset hasActed for new betting round
    const resetPlayers = players.map(p => ({ 
      ...p, 
      hasActed: false,
      currentBet: gamePhase !== GAME_PHASES.PREFLOP ? 0 : p.currentBet // Reset bets for new rounds
    }));
    
    // Recalculate current bet for post-flop rounds
    const newCurrentBet = gamePhase === GAME_PHASES.PREFLOP ? get().currentBet : 0;
    
    set({
      players: resetPlayers,
      currentPlayerIndex: firstToAct,
      currentBet: newCurrentBet,
      message: resetPlayers[firstToAct].name === 'You' ? 'Your turn' : `${resetPlayers[firstToAct].name}'s turn`
    });
    
    // Check if only one player can act
    const canAct = resetPlayers.filter(p => !p.folded && !p.isAllIn);
    if (canAct.length <= 1) {
      get().advancePhase();
      return;
    }
    
    if (!resetPlayers[firstToAct].isHuman) {
      setTimeout(() => get().aiAction(), 1000);
    }
  },
  
  getValidActions: () => {
    const { players, currentPlayerIndex, currentBet } = get();
    const player = players[currentPlayerIndex];
    
    if (!player || player.folded || player.isAllIn) return [];
    
    const actions = ['fold'];
    const toCall = currentBet - player.currentBet;
    
    if (toCall === 0) {
      actions.push('check');
    } else if (player.chips >= toCall) {
      actions.push('call');
    }
    
    if (player.chips > toCall) {
      actions.push('raise');
    }
    
    if (player.chips > 0) {
      actions.push('all_in');
    }
    
    return actions;
  },
  
  executeAction: (action, raiseAmount = 0) => {
    const { players, currentPlayerIndex, currentBet, pot, minRaise, bigBlind, actionLog, currentHandActions } = get();
    const player = { ...players[currentPlayerIndex] };
    const toCall = currentBet - player.currentBet;
    
    let newPot = pot;
    let newCurrentBet = currentBet;
    let newMinRaise = minRaise;
    const newLog = [...actionLog];
    const newHandActions = [...currentHandActions];
    
    let logEntry = '';
    let chipAnimAmount = 0; // Track amount for chip animation
    // Grammar helper: "You fold" vs "Morgan folds"
    const verb = (base) => player.name === 'You' ? base : base + 's';
    
    switch (action) {
      case 'fold':
        player.folded = true;
        logEntry = `${player.name} ${verb('fold')}`;
        PokerSounds.playDeal();
        break;
        
      case 'check':
        logEntry = `${player.name} ${verb('check')}`;
        PokerSounds.playChip();
        break;
        
      case 'call':
        const callAmount = Math.min(toCall, player.chips);
        player.chips -= callAmount;
        player.currentBet += callAmount;
        player.totalBet += callAmount;
        newPot += callAmount;
        chipAnimAmount = callAmount;
        if (player.chips === 0) player.isAllIn = true;
        logEntry = `${player.name} ${verb('call')} ${callAmount}`;
        PokerSounds.playChip();
        break;
        
      case 'raise':
        const totalRaise = toCall + raiseAmount;
        const actualRaise = Math.min(totalRaise, player.chips);
        player.chips -= actualRaise;
        player.currentBet += actualRaise;
        player.totalBet += actualRaise;
        newPot += actualRaise;
        chipAnimAmount = actualRaise;
        newCurrentBet = player.currentBet;
        newMinRaise = Math.max(bigBlind, raiseAmount);
        if (player.chips === 0) player.isAllIn = true;
        logEntry = `${player.name} ${verb('raise')} to ${player.currentBet}`;
        PokerSounds.playChip();
        break;
        
      case 'all_in':
        const allInAmount = player.chips;
        player.totalBet += allInAmount;
        player.currentBet += allInAmount;
        newPot += allInAmount;
        chipAnimAmount = allInAmount;
        player.chips = 0;
        player.isAllIn = true;
        if (player.currentBet > newCurrentBet) {
          newCurrentBet = player.currentBet;
          newMinRaise = Math.max(bigBlind, player.currentBet - currentBet);
        }
        logEntry = `${player.name} ALL IN (${allInAmount})`;
        PokerSounds.playChip();
        break;
    }
    
    player.hasActed = true;
    newLog.push(logEntry);
    newHandActions.push(logEntry);
    
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = player;
    
    // Reset hasActed if raise
    if (action === 'raise' || (action === 'all_in' && player.currentBet > currentBet)) {
      newPlayers.forEach((p, i) => {
        if (i !== currentPlayerIndex && !p.folded && !p.isAllIn) {
          newPlayers[i] = { ...p, hasActed: false };
        }
      });
    }
    
    // Trigger chip animation if chips were bet
    const chipAnim = chipAnimAmount > 0 ? { fromPosition: currentPlayerIndex, amount: chipAnimAmount } : null;
    
    set({
      players: newPlayers,
      pot: newPot,
      currentBet: newCurrentBet,
      minRaise: newMinRaise,
      actionLog: newLog,
      currentHandActions: newHandActions,
      chipAnimation: chipAnim
    });
    
    setTimeout(() => get().checkBettingComplete(), 300);
  },
  
  playerAction: (action, raiseAmount = 0) => {
    const { players, currentPlayerIndex } = get();
    if (!players[currentPlayerIndex]?.isHuman) return;
    get().executeAction(action, raiseAmount);
  },
  
  checkBettingComplete: () => {
    const { players, currentBet, gamePhase } = get();
    
    const activePlayers = players.filter(p => !p.folded && !p.isAllIn);
    const playersInHand = players.filter(p => !p.folded);
    
    if (playersInHand.length === 1) {
      get().endHandEarly(playersInHand[0]);
      return;
    }
    
    const allMatched = activePlayers.every(p => p.hasActed && p.currentBet === currentBet);
    
    if (allMatched || activePlayers.length === 0) {
      get().advancePhase();
    } else {
      get().nextPlayer();
    }
  },
  
  nextPlayer: () => {
    const { players, currentPlayerIndex, currentBet } = get();
    
    let nextIndex = (currentPlayerIndex + 1) % players.length;
    let attempts = 0;
    
    while ((players[nextIndex].folded || players[nextIndex].isAllIn || 
           (players[nextIndex].hasActed && players[nextIndex].currentBet === currentBet)) 
           && attempts < players.length) {
      nextIndex = (nextIndex + 1) % players.length;
      attempts++;
    }
    
    if (attempts >= players.length) {
      get().advancePhase();
      return;
    }
    
    set({
      currentPlayerIndex: nextIndex,
      message: players[nextIndex].name === 'You' ? 'Your turn' : `${players[nextIndex].name}'s turn`
    });
    
    if (!players[nextIndex].isHuman) {
      setTimeout(() => get().aiAction(), 800);
    }
  },
  
  aiAction: () => {
    const { players, currentPlayerIndex, currentBet, bigBlind, communityCards, pot } = get();
    const player = players[currentPlayerIndex];
    
    if (player.isHuman || player.folded || player.isAllIn) return;
    
    // Evaluate hand strength
    const allCards = [...player.holeCards, ...communityCards];
    let handStrength = 0.3; // Default for pre-flop
    
    if (allCards.length >= 5) {
      const best = bestFiveCardHand(allCards);
      handStrength = best.evaluation.hand.rank / 10;
    } else if (player.holeCards.length === 2) {
      // Pre-flop hand strength estimation
      const [c1, c2] = player.holeCards;
      const isPair = c1.rank === c2.rank;
      const isSuited = c1.suit === c2.suit;
      const highCard = Math.max(c1.rank, c2.rank);
      const gap = Math.abs(c1.rank - c2.rank);
      
      handStrength = 0.2;
      if (isPair) handStrength += 0.3 + (c1.rank / 14) * 0.2;
      if (highCard >= 12) handStrength += 0.15;
      if (isSuited) handStrength += 0.1;
      if (gap <= 2) handStrength += 0.1;
    }
    
    const toCall = currentBet - player.currentBet;
    const potOdds = pot > 0 ? toCall / (pot + toCall) : 0;
    
    let action = 'fold';
    let raiseAmount = 0;
    
    if (toCall === 0) {
      if (handStrength > 0.5 && Math.random() < 0.6) {
        action = 'raise';
        raiseAmount = bigBlind * Math.floor(1 + handStrength * 3);
      } else {
        action = 'check';
      }
    } else {
      if (handStrength > 0.6) {
        if (Math.random() < handStrength * 0.7) {
          action = 'raise';
          raiseAmount = Math.min(toCall * 2, player.chips - toCall);
          if (raiseAmount <= 0) action = 'call';
        } else {
          action = 'call';
        }
      } else if (handStrength > 0.35 && potOdds < handStrength) {
        action = 'call';
      } else if (toCall <= bigBlind * 2 && handStrength > 0.25) {
        action = Math.random() < 0.4 ? 'call' : 'fold';
      }
    }
    
    if (action === 'call' && player.chips < toCall) {
      action = player.chips > 0 ? 'all_in' : 'fold';
    }
    
    get().executeAction(action, raiseAmount);
  },
  
  advancePhase: () => {
    const { gamePhase, deck, communityCards, actionLog, currentHandActions } = get();
    
    let newPhase = gamePhase;
    let newCommunity = [...communityCards];
    let newDeck = [...deck];
    const newLog = [...actionLog];
    const newHandActions = [...currentHandActions];
    
    switch (gamePhase) {
      case GAME_PHASES.PREFLOP:
        // Deal flop (3 cards)
        const flop = dealCards(newDeck, 3);
        newCommunity = flop.dealt;
        newDeck = flop.remaining;
        newPhase = GAME_PHASES.FLOP;
        newLog.push('--- Flop ---');
        newHandActions.push('--- Flop ---');
        PokerSounds.playFlip();
        break;
        
      case GAME_PHASES.FLOP:
        // Deal turn (1 card)
        const turn = dealCards(newDeck, 1);
        newCommunity = [...newCommunity, ...turn.dealt];
        newDeck = turn.remaining;
        newPhase = GAME_PHASES.TURN;
        newLog.push('--- Turn ---');
        newHandActions.push('--- Turn ---');
        PokerSounds.playFlip();
        break;
        
      case GAME_PHASES.TURN:
        // Deal river (1 card)
        const river = dealCards(newDeck, 1);
        newCommunity = [...newCommunity, ...river.dealt];
        newDeck = river.remaining;
        newPhase = GAME_PHASES.RIVER;
        newLog.push('--- River ---');
        newHandActions.push('--- River ---');
        PokerSounds.playFlip();
        break;
        
      case GAME_PHASES.RIVER:
        // Go to showdown
        newPhase = GAME_PHASES.SHOWDOWN;
        newLog.push('--- Showdown ---');
        newHandActions.push('--- Showdown ---');
        break;
    }
    
    set({
      gamePhase: newPhase,
      communityCards: newCommunity,
      deck: newDeck,
      actionLog: newLog,
      currentHandActions: newHandActions,
      message: newPhase === GAME_PHASES.SHOWDOWN ? 'Showdown!' : `${newPhase.charAt(0).toUpperCase() + newPhase.slice(1)} betting`
    });
    
    if (newPhase === GAME_PHASES.SHOWDOWN) {
      setTimeout(() => get().goToShowdown(), 500);
    } else if (newPhase !== GAME_PHASES.RESULT) {
      setTimeout(() => get().startBettingRound(), 1000);
    }
  },
  
  endHandEarly: (winner) => {
    const { pot, actionLog, currentHandActions, handsPlayed, handsWon, players, handHistory } = get();
    
    const newPlayers = players.map(p => 
      p.id === winner.id ? { ...p, chips: p.chips + pot } : p
    );
    
    // Grammar fix: "You win" vs "Name wins"
    const winVerb = winner.name === 'You' ? 'win' : 'wins';
    const finalLog = [...actionLog, `${winner.name} ${winVerb} ${pot} (others folded)`];
    const finalHandActions = [...currentHandActions, `${winner.name} ${winVerb} ${pot}`];
    
    // Save to hand history
    const handRecord = {
      id: Date.now(),
      actions: finalHandActions,
      winner: winner.name,
      pot,
      timestamp: new Date().toISOString()
    };
    
    PokerSounds.playWin();
    
    set({
      players: newPlayers,
      winners: [winner],
      gamePhase: GAME_PHASES.RESULT,
      actionLog: finalLog,
      message: `${winner.name} ${winVerb} ${pot}!`,
      handsPlayed: handsPlayed + 1,
      handsWon: winner.isHuman ? handsWon + 1 : handsWon,
      handHistory: [...handHistory, handRecord]
    });
  },
  
  goToShowdown: () => {
    const { players, communityCards } = get();
    
    const playersWithHands = players.map(p => {
      if (p.folded) return { ...p, hand: null };
      const allCards = [...p.holeCards, ...communityCards];
      const best = bestFiveCardHand(allCards);
      return { ...p, hand: best.evaluation };
    });
    
    set({
      players: playersWithHands,
      showdownRevealed: true
    });
    
    setTimeout(() => get().determineWinner(), 1500);
  },
  
  determineWinner: () => {
    const { players, pot, actionLog, currentHandActions, handsPlayed, handsWon, handHistory } = get();
    
    const activePlayers = players.filter(p => !p.folded);
    
    let bestScore = -1;
    let winners = [];
    
    activePlayers.forEach(p => {
      if (p.hand && p.hand.score > bestScore) {
        bestScore = p.hand.score;
        winners = [p];
      } else if (p.hand && p.hand.score === bestScore) {
        winners.push(p);
      }
    });
    
    const share = Math.floor(pot / winners.length);
    const newPlayers = players.map(p => {
      const isWinner = winners.some(w => w.id === p.id);
      return isWinner ? { ...p, chips: p.chips + share } : p;
    });
    
    const winnerNames = winners.map(w => w.name).join(', ');
    const winningHand = winners[0]?.hand?.hand?.name || 'Unknown';
    
    // Grammar fix: "You win" vs "Name wins"
    const isSingleHumanWinner = winners.length === 1 && winners[0].name === 'You';
    const winVerb = isSingleHumanWinner ? 'win' : 'wins';
    const resultLog = `${winnerNames} ${winVerb} ${share} with ${winningHand}`;
    const finalLog = [...actionLog, resultLog];
    const finalHandActions = [...currentHandActions, resultLog];
    
    const humanWon = winners.some(w => w.isHuman);
    
    // Save to hand history
    const handRecord = {
      id: Date.now(),
      actions: finalHandActions,
      winner: winnerNames,
      winningHand,
      pot,
      communityCards: get().communityCards,
      timestamp: new Date().toISOString()
    };
    
    if (humanWon) {
      PokerSounds.playWin();
    } else {
      PokerSounds.playLose();
    }
    
    set({
      players: newPlayers,
      winners,
      gamePhase: GAME_PHASES.RESULT,
      actionLog: finalLog,
      message: `${winnerNames} ${winVerb} with ${winningHand}!`,
      handsPlayed: handsPlayed + 1,
      handsWon: humanWon ? handsWon + 1 : handsWon,
      handHistory: [...handHistory, handRecord]
    });
  },
  
  nextHand: () => {
    set({
      gamePhase: GAME_PHASES.WAITING,
      message: 'Starting next hand...',
      currentHandActions: []
    });
    // Auto-start the next hand with rotated dealer
    setTimeout(() => get().startHand(), 300);
  },
  
  resetGame: () => {
    set(INITIAL_STATE);
  }
}));

export default useHoldemStore;
